import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import DashboardInspection from "./pages/DashboardInspection";
import DashboardProduksiMap from "./pages/DashboardProduksiMap";
import DashboardProduksi from "./pages/DashboardProduksi";
import EstateDashboard from "./pages/EstateDashboard";
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from "@vercel/speed-insights/react"
import CategoryPermissionsPage from "./pages/CategoryPermissionsPage";
import HotspotsPage from './pages/HotspotsPage';
import WaterLevelPage from "./pages/WaterLevelPage";
import SandboxPage from "./pages/SandboxPage";
import SandboxPageExample from "./pages/SandboxPageExample";
import LoginPage from "./auth/LoginPage";
import { AuthProvider, ProtectedRoute } from "./auth/AuthContext";
import HarvestersPerformancePage from "./pages/HarvestersPerformancePage";
import AdminDashboardPage from "./admin/pages/AdminDashboardPage";
import FeedPage from "./pages/FeedPage";
import HarvestComparisonPage from "./pages/HarvestComparisonPage";
import { MutuAncakPage } from "./operational/mutu_ancak";
import { MutuBuahPage } from "./operational/mutu_buah";
import { RKHPage, RKHDetailPage } from "./operational/rkh";
import { TaksasiPage } from "./operational/taksasi";
import { UpkeepMonitoringPage } from "./operational/budget/upkeep_monitoring";
import { ProductionMonitoringPage } from "./operational/budget/production_monitoring";
import { HarvestMonitoringPage } from "./operational/harvest";
import { HarvestMonitoringForemanPage } from "./operational/harvest-monitoring-foreman";
import { BkmMuatPage } from "./operational/bkm-muat";
import { HarvestMapPage } from "./operational/harvest_map";
import { UpkeepMapPage } from "./operational/upkeep_map";
import { OdooVsSaktiPage } from "./operational/odoo_vs_sakti";
import { FuelConsumptionPage } from "./operational/fuel_consumption";
import { WeighbridgePage } from "./operational/weighbridge";
import { CashFlowDashboardPage } from "./finance";
import { RbacProvider } from "./rbac/RbacContext";
import { ActivityTracker } from "./activity";
import ForbiddenPage from "./pages/ForbiddenPage";
import { RequirePermission } from "./rbac/routeGuards";
import ApplicationsPage from "./admin/pages/ApplicationsPage";
import MenusPage from "./admin/pages/MenusPage";
import RolesPage from "./admin/pages/RolesPage";
import RoleDetailPage from "./admin/pages/RoleDetailPage";
import UsersPage from "./admin/pages/UsersPage";
import UserDetailPage from "./admin/pages/UserDetailPage";
import UserOverridesPage from "./admin/pages/UserOverridesPage";
import AdminLayout from "./admin/components/AdminLayout";
import SmartHomePage from "./pages/SmartHomePage";
import AdminGuard from "./admin/components/AdminGuard";
import {
    SaktiPage,
    SaktiRkhPage,
    SaktiAccessCodePage,
    SaktiBccPage,
    SaktiSpbPage,
    SaktiBkmPage,
    SaktiConfigurationPage,
    SaktiPushOdooPage,
    SaktiBccImageDownloadPage,
    SaktiFaceRegistryPage,
    SaktiLivenessAuditPage,
    SaktiAppConfigPage,
} from "./sakti";
import SaktiRemoteInspectionPage from "./sakti/pages/SaktiRemoteInspectionPage";


const App: React.FC = () => {
    return (
        <>
            <Router>
                <ActivityTracker />
                <AuthProvider>
                    <RbacProvider>
                    <Routes>
                        {/* Public route */}
                        <Route path="/login" element={<LoginPage />} />
                        
                        {/* Protected routes */}
                        <Route path="/" element={
                            <ProtectedRoute>
                                <SmartHomePage />
                            </ProtectedRoute>
                        } />
                        <Route path="/peta-inspeksi" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/peta-inspeksi">
                                    <DashboardInspection />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/estate-map" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/estate-map">
                                    <DashboardProduksiMap />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/hotspot" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/hotspot">
                                    <HotspotsPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/wms" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/wms">
                                    <WaterLevelPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/mill-dashboard" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/mill-dashboard">
                                    <DashboardProduksi />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/production-dashboard" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/production-dashboard">
                                    <DashboardProduksi />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/estate-dashboard" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/estate-dashboard">
                                    <EstateDashboard />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/harvesters-performance" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/harvesters-performance">
                                    <HarvestersPerformancePage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/feeds" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/feeds">
                                    <FeedPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/harvest-comparison" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/harvest-comparison">
                                    <HarvestComparisonPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/user/category-permissions" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/user/category-permissions">
                                    <CategoryPermissionsPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/admin">
                                    <AdminDashboardPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/rkh" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiRkhPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/access-code" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiAccessCodePage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/bcc" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiBccPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/spb" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiSpbPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/bkm" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiBkmPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/configuration" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiConfigurationPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/push-odoo" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiPushOdooPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/bcc-images" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiBccImageDownloadPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/face-registry" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiFaceRegistryPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/attendance-audit" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiLivenessAuditPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/app-config" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiAppConfigPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/sakti/remote-inspection" element={
                            <ProtectedRoute>
                                <AdminGuard>
                                    <SaktiRemoteInspectionPage />
                                </AdminGuard>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/applications" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/admin/applications">
                                    <AdminLayout>
                                        <ApplicationsPage />
                                    </AdminLayout>
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/menus" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/admin/menus">
                                    <AdminLayout>
                                        <MenusPage />
                                    </AdminLayout>
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/roles" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/admin/roles">
                                    <AdminLayout>
                                        <RolesPage />
                                    </AdminLayout>
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/roles/:id" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/admin/roles">
                                    <AdminLayout>
                                        <RoleDetailPage />
                                    </AdminLayout>
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/users" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/admin/users">
                                    <AdminLayout>
                                        <UsersPage />
                                    </AdminLayout>
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/users/:id" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/admin/users">
                                    <AdminLayout>
                                        <UserDetailPage />
                                    </AdminLayout>
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/users/:id/overrides" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/admin/users">
                                    <AdminLayout>
                                        <UserOverridesPage />
                                    </AdminLayout>
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/mutu-ancak" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/mutu-ancak">
                                    <MutuAncakPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/mutu-buah" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/mutu-buah">
                                    <MutuBuahPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/rkh" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/rkh">
                                    <RKHPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/rkh/detail" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/rkh/detail">
                                    <RKHDetailPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/taksasi" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/taksasi">
                                    <TaksasiPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/upkeep-monitoring" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/upkeep-monitoring">
                                    <UpkeepMonitoringPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/production-monitoring" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/production-monitoring">
                                    <ProductionMonitoringPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/harvest-monitoring" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/harvest-monitoring">
                                    <HarvestMonitoringPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/harvest-monitoring-foreman" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/harvest-monitoring-foreman">
                                    <HarvestMonitoringForemanPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/bkm-muat" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/bkm-muat">
                                    <BkmMuatPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/harvest-map" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/harvest-map">
                                    <HarvestMapPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/upkeep-map" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/upkeep-map">
                                    <UpkeepMapPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/odoo-vs-sakti" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/odoo-vs-sakti">
                                    <OdooVsSaktiPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/fuel_consumption_vs_lacak" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/fuel_consumption_vs_lacak">
                                    <FuelConsumptionPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/operational/weighbridge" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/operational/weighbridge">
                                    <WeighbridgePage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        <Route path="/finance/cash-flow" element={
                            <ProtectedRoute>
                                <RequirePermission permission="/finance/cash-flow">
                                    <CashFlowDashboardPage />
                                </RequirePermission>
                            </ProtectedRoute>
                        } />
                        {/* <Route path="/user/management/inspections" element={<InspectionsManagementPage />} /> */}
                        <Route path="/sandbox-example" element={
                            <ProtectedRoute>
                                <SandboxPageExample />
                            </ProtectedRoute>
                        } />
                        <Route path="/sandbox" element={
                            <ProtectedRoute>
                                <SandboxPage />
                            </ProtectedRoute>
                        } />
                        {/* Generic forbidden page for permission guard */}
                        <Route path="/403" element={<ForbiddenPage />} />
                        {/* <Route path="/inspections-management" element={
                            <ProtectedRoute>
                                <InspectionsManagementPage />
                            </ProtectedRoute>
                        } /> */}
                    </Routes>
                    </RbacProvider>
                </AuthProvider>
            </Router>
            <Analytics />
            <SpeedInsights />
        </>
    );
};

export default App;
